// /admin/recipes/:id/edit: the existing recipe edit forms, inside the new admin layout,
// each with its own Save button and a message after every save.
import { Link, useParams } from 'react-router-dom'

import { getRecipe, updateRecipe, updateRecipePhoto } from '../../../api/adminV2'
import { DRAFT_STATUS } from '../../../utils/constants'
import ChangeRecipeName from '../../AdminPanel/components/ChangeRecipeName'
import RecipePhotoChange from '../../AdminPanel/components/RecipePhotoChange'
import SummaryChanges from '../../AdminPanel/components/SummaryChanges'
import IngredientsForm from '../../AdminPanel/components/IngredientsForm'
import StepsForm from '../../AdminPanel/components/StepsForm'
import { LoadError, Loading, useFeedback, useLoad } from '../ui'

import '../../AdminPanel/style.css'

const RecipeEdit = () => {
  const { id } = useParams()
  const { toast, toastError } = useFeedback()
  const { data: recipe, error, loading, reload } = useLoad(() => getRecipe(id), [id])

  if (error && !recipe) return <LoadError error={error} onRetry={reload} />
  if (loading && !recipe) return <Loading />
  if (!recipe) return null

  // Drafts may be saved half-finished; published recipes must stay complete
  const required = Number(recipe.status) !== DRAFT_STATUS
  const feedback = {
    save: updateRecipe,
    onSaved: (message) => toast(message || 'Saved'),
    onError: (e) => toastError(e, 'That did not save. Please try again.'),
    onInvalid: () =>
      toastError('Nothing to save yet, or a field needs fixing (it is outlined in red).'),
  }
  const props = { dataRecipe: recipe, required, idRecipe: id, ...feedback }

  return (
    <>
      <div>
        <Link className="a-crumb" to={`/admin/recipes/${id}`}>
          ← {recipe.name || 'Recipe'}
        </Link>
        <div className="a-head">
          <div>
            <h1>Edit recipe</h1>
            <p className="a-sub">
              Each part saves on its own: change it, then press the pencil button next to its title.
              {required ? '' : ' This is a draft, so empty parts are allowed.'}
            </p>
          </div>
          <Link className="a-btn" to={`/admin/recipes/${id}`}>
            Done
          </Link>
        </div>
      </div>

      <section className="a-panel a-edit-panel">
        <h2>Name</h2>
        <ChangeRecipeName
          idRecipe={id}
          to_publish={required}
          value={recipe.name}
          save={updateRecipe}
          onSaved={feedback.onSaved}
          onError={feedback.onError}
          onInvalid={feedback.onInvalid}
        />
      </section>

      <section className="a-panel a-edit-panel">
        <RecipePhotoChange
          idRecipe={id}
          to_publish={required}
          value={recipe.image}
          upload={updateRecipePhoto}
          onSaved={feedback.onSaved}
          onError={feedback.onError}
        />
      </section>

      <section className="a-panel a-edit-panel">
        <SummaryChanges {...props} />
      </section>

      <section className="a-panel a-edit-panel">
        <IngredientsForm {...props} required={false} toPublish={required} />
      </section>

      <section className="a-panel a-edit-panel">
        <StepsForm {...props} />
      </section>
    </>
  )
}

export default RecipeEdit
